import React from "react";

import {
  faCircleInfo,
  faPlus,
  faTrashCan,
} from "@fortawesome/pro-regular-svg-icons";
import { map } from "lodash";
import { UseFormReturn, useFieldArray } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { FieldErrors } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Icon } from "src/design-system/Icon";
import { Tooltip } from "src/design-system/Tooltip";
import { InputMappingRow } from "src/integrationNode/editorComponents/InputMappingRow";
import {
  FIELD_ARRAY_KEY_NAME,
  groupClassName,
} from "src/integrationNode/editorComponents/InputMappings";
import {
  InputMappingList as InputMappingListType,
  InputMappings,
} from "src/integrationNode/types";

export type Props = {
  list: InputMappingListType;
  listName: string;
  formProps: UseFormReturn<InputMappings>;
  groupedGroupsAndLists?: boolean;
  runFieldErrors?: FieldErrors;
  immutable: boolean;
  nestedGroupName?: string;
};

export const InputMappingList: React.FC<Props> = ({
  list,
  listName,
  formProps,
  groupedGroupsAndLists,
  runFieldErrors,
  immutable,
  nestedGroupName,
}) => {
  const { fields, append, remove } = useFieldArray({
    control: formProps.control,
    name: nestedGroupName
      ? `grouped.${nestedGroupName}.lists.${listName}.elements`
      : `lists.${listName}.elements`,
    keyName: FIELD_ARRAY_KEY_NAME,
  });
  const { rules, elements, displayName, description, getDefaultElement } = list;
  const shouldDisableDeletion =
    (rules?.required && elements.length <= 1) ||
    (!!rules?.minLength && elements.length <= rules.minLength);

  const shouldDisableAddition =
    !!rules?.maxLength && elements.length >= rules.maxLength;

  return (
    <>
      {fields.map((element, index) => (
        <div
          key={element.reactHookFormsKey}
          className={twMerge(
            groupClassName,
            "font-inter-normal-12px text-gray-700",
          )}
        >
          <div className="mb-4 flex items-center justify-between border-gray-200 border-b pb-2">
            <div className="flex items-center gap-2 font-inter-normal-12px text-gray-700">
              {`${displayName.toUpperCase()} ${index + 1}`}
              {!!description && (
                <Tooltip body={description}>
                  <div className="inline-block">
                    <Icon
                      variant="default"
                      dataLoc="list-description-icon"
                      icon={faCircleInfo}
                      size="xs"
                    />
                  </div>
                </Tooltip>
              )}
            </div>
            <Button
              disabled={immutable || shouldDisableDeletion}
              iconLeft={faTrashCan}
              size="sm"
              variant="secondary"
              onClick={() => remove(index)}
            >
              Delete {displayName}
            </Button>
          </div>
          {map(element, (inputMapping, beName) => {
            if (beName !== FIELD_ARRAY_KEY_NAME) {
              return (
                <InputMappingRow
                  key={beName}
                  beName={`${listName}.${beName}`}
                  formControl={formProps.control}
                  immutable={immutable}
                  inputMapping={inputMapping}
                  inputProps={formProps.register(
                    nestedGroupName
                      ? `grouped.${nestedGroupName}.lists.${listName}.elements.${index}.${beName}.assignedTo`
                      : `lists.${listName}.elements.${index}.${beName}.assignedTo`,
                  )}
                  runFieldError={runFieldErrors?.[inputMapping.id]}
                />
              );
            }
            return null;
          })}
        </div>
      ))}
      {!groupedGroupsAndLists && (
        <span className="mr-1 mb-4 inline-block">
          <Button
            dataLoc={`add-list-${listName}`}
            disabled={immutable || shouldDisableAddition}
            iconLeft={faPlus}
            size="sm"
            variant="secondary"
            onClick={() => append(getDefaultElement())}
          >
            Add {displayName}
          </Button>
        </span>
      )}
    </>
  );
};
